import { Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { Clock } from "lucide-react";
import { CoverPattern } from "./CoverPattern";
import type { BlogPostWithRelations } from "@/types/blog";

const trackBadge: Record<string, { label: string; className: string }> = {
  office: { label: "Office", className: "bg-sky/20 text-navy" },
  kitchen: { label: "Kitchen", className: "bg-butter text-navy" },
  both: { label: "Both", className: "bg-charcoal/10 text-charcoal" },
};

interface BlogPostCardProps {
  post: BlogPostWithRelations;
}

export function BlogPostCard({ post }: BlogPostCardProps) {
  const t = trackBadge[post.track] || trackBadge.both;
  const authorName = post.blog_authors?.name || "Lembra";
  const categoryName = post.blog_categories?.name;
  const authorAvatar = post.blog_authors?.avatar_url;

  return (
    <Link
      to={`/blog/${post.slug}`}
      className={`group block bg-white rounded-2xl overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.1)] transition-all duration-300 hover:-translate-y-1 ${
        post.is_cornerstone ? "border-l-[3px] border-l-[#D4A844]" : ""
      }`}
    >
      {/* Cover image */}
      <div className="overflow-hidden">
        {post.cover_image_url ? (
          <img
            src={post.cover_image_url}
            alt={post.cover_image_alt || post.title}
            loading="lazy"
            className="w-full aspect-video object-cover transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.03]"
          />
        ) : (
          <CoverPattern
            track={post.track}
            seed={post.slug}
            className="transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.03]"
          />
        )}
      </div>

      {/* Content */}
      <div className="p-5 space-y-3">
        {/* Meta line */}
        <div className="flex items-center gap-2 text-xs">
          {categoryName && (
            <span className="font-semibold uppercase tracking-[0.05em] text-charcoal/50">
              {categoryName}
            </span>
          )}
          {categoryName && (
            <span className="text-charcoal/30">·</span>
          )}
          <span className="flex items-center gap-0.5 text-charcoal/40">
            <Clock className="h-3 w-3" /> {post.reading_time_minutes} min read
          </span>
        </div>

        {/* Title */}
        <h3 className="font-heading text-lg text-navy leading-snug line-clamp-2">
          {post.title}
        </h3>

        {/* Excerpt */}
        {post.excerpt && (
          <p className="text-sm text-charcoal/60 leading-relaxed line-clamp-3">
            {post.excerpt}
          </p>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-1">
          <div className="flex items-center gap-2 text-xs text-charcoal/50">
            {authorAvatar ? (
              <img src={authorAvatar} alt={authorName} className="h-5 w-5 rounded-full object-cover" />
            ) : (
              <div className="h-5 w-5 rounded-full bg-sky/20 flex items-center justify-center text-navy text-[10px] font-bold">
                {authorName.charAt(0)}
              </div>
            )}
            <span>{authorName}</span>
            {post.published_at && (
              <>
                <span className="text-charcoal/30">·</span>
                <span>{format(parseISO(post.published_at), "MMM yyyy")}</span>
              </>
            )}
          </div>
          <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${t.className}`}>
            {t.label}
          </span>
        </div>
      </div>
    </Link>
  );
}
